import { db } from "../firebaseClient"
import { doc, setDoc, deleteDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"

// Minimal webhook system: an org registers an endpoint + which events it
// cares about, dispatchWebhook fires a real POST when that event happens,
// and every attempt (success or failure) is logged for the health card.
export const createWebhookEndpoint = async ({ orgId, url, events }) => {
  if (!orgId || !url?.trim()) return { endpointId: null, error: { message: "URL is required" } }
  try {
    const ref = doc(collection(db, "organizations", orgId, "webhookEndpoints"))
    await setDoc(ref, {
      url: url.trim(),
      events: events || [],
      status: "active",
      createdAt: serverTimestamp(),
    })
    return { endpointId: ref.id, error: null }
  } catch (error) {
    return { endpointId: null, error }
  }
}

export const deleteWebhookEndpoint = async (orgId, endpointId) => {
  try {
    await deleteDoc(doc(db, "organizations", orgId, "webhookEndpoints", endpointId))
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const listWebhookEndpoints = async (orgId) => {
  try {
    const ref = collection(db, "organizations", orgId, "webhookEndpoints")
    const snap = await getDocs(ref)
    return { endpoints: snap.docs?.map((d) => ({ endpointId: d.id, ...d.data() })), error: null }
  } catch (error) {
    return { endpoints: [], error }
  }
}

// Logs one delivery attempt — success or failure — so the health card
// reflects real dispatch outcomes, not just registered endpoints existing.
const logWebhookDelivery = async ({ orgId, endpointId, event, status, responseCode }) => {
  try {
    const ref = doc(collection(db, "webhookDeliveries"))
    await setDoc(ref, {
      orgId,
      endpointId,
      event,
      status, // "success" | "failed"
      responseCode: responseCode ?? null,
      createdAt: serverTimestamp(),
    })
  } catch {
    // Best-effort logging — a logging failure shouldn't break the caller.
  }
}

// Fires a real POST to every active endpoint subscribed to this event type.
// Fire-and-forget from the caller's perspective — never throws, never blocks
// the action that triggered it (course completion, cert issuance, etc.).
// NOTE: client-side dispatch is a known limitation (CORS, no retry/backoff) —
// same accepted tradeoff as client-side grading elsewhere in this app. A
// production version belongs in a Cloud Function.
export const dispatchWebhook = async (orgId, eventType, payload) => {
  if (!orgId) return
  try {
    const { endpoints } = await listWebhookEndpoints(orgId)
    const targets = endpoints?.filter((e) => e?.status === "active" && e?.events?.includes(eventType)) || []

    await Promise.all(
      targets?.map(async (endpoint) => {
        try {
          const response = await fetch(endpoint?.url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ event: eventType, orgId, data: payload, sentAt: new Date().toISOString() }),
          })
          await logWebhookDelivery({
            orgId,
            endpointId: endpoint?.endpointId,
            event: eventType,
            status: response?.ok ? "success" : "failed",
            responseCode: response?.status,
          })
        } catch {
          await logWebhookDelivery({ orgId, endpointId: endpoint?.endpointId, event: eventType, status: "failed", responseCode: null })
        }
      })
    )
  } catch {
    // Listing endpoints failed — nothing to dispatch, nothing to log.
  }
}

export const listOrgWebhookDeliveries = async (orgId) => {
  try {
    const ref = collection(db, "webhookDeliveries")
    const q = query(ref, where("orgId", "==", orgId))
    const snap = await getDocs(q)
    return { deliveries: snap.docs?.map((d) => d.data()), error: null }
  } catch (error) {
    return { deliveries: [], error }
  }
}